/**
 * The maths behind `TrendChart` — axis ticks, label thinning, marker sizing and path building.
 *
 * Split out of the component for the same reason as `controls.model.ts`: the admin suite runs
 * without jsdom (see `vitest.config.ts`), and this is the part worth testing. An analytics card is
 * ~240px wide with three of them side by side, so "which labels fit" is not a styling detail — a
 * 90-day range that prints every date is an unreadable smear, and a 7-day range that prints only
 * the two ends (what shipped before) is what QA reported: "no dates in between".
 *
 * Everything here is pure and unit-free: callers pass pixels, they get pixels back.
 */

/** A point in plot space (pixels, y already inverted for SVG). */
export interface Point {
  x: number;
  y: number;
}

/** Approximate advance width of one digit at the 10px axis font, in px. */
export const AXIS_CHAR_W = 6;

/**
 * Horizontal room one rotated x-axis label needs before its neighbour starts to touch it.
 *
 * `MM-DD` at the 10px axis font is ~30px long, and rotating it 45° projects that onto ~21px of
 * x — which is why the labels are rotated at all. 23 keeps a hair of daylight between them.
 */
const X_LABEL_SLOT = 23;

/**
 * Y-axis ticks from `0` to a "nice" ceiling at or above `max`.
 *
 * Steps come from the 1 / 2 / 5 × 10ⁿ family so the axis reads as round numbers, and `integer`
 * forces a whole-number step — a count series (form opens, orders) must never print `0.5` orders.
 * The last tick is always ≥ `max`, so the caller can scale the plot to `ticks[ticks.length - 1]`
 * and know the line fits inside it.
 */
export function niceTicks(
  max: number,
  opts: { intervals?: number; integer?: boolean } = {},
): number[] {
  const intervals = Math.max(2, Math.min(6, opts.intervals ?? 4));
  if (!Number.isFinite(max) || max <= 0) return [0, 1];

  const raw = max / intervals;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalised = raw / magnitude;
  let step = (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10) * magnitude;
  if (opts.integer) step = Math.max(1, Math.ceil(step));

  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  // Float steps accumulate error (0.1 + 0.2 …), so snap every value back to 12 significant digits.
  for (let i = 0; i * step <= top + step / 2; i += 1)
    ticks.push(Number((i * step).toPrecision(12)));
  return ticks;
}

/**
 * A y-axis tick as text: compact for money (`1.5K`, `2.4M`), plain for counts.
 *
 * Revenue in COP or CLP reaches eight digits, and a left gutter wide enough for `12.500.000`
 * would leave no plot to speak of in a 240px card.
 */
export function formatTickValue(value: number): string {
  const abs = Math.abs(value);
  const trim = (n: number): string => String(Number(n.toFixed(1)));
  if (abs >= 1e9) return `${trim(value / 1e9)}B`;
  if (abs >= 1e6) return `${trim(value / 1e6)}M`;
  if (abs >= 1e3) return `${trim(value / 1e3)}K`;
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(2)));
}

/** Width of the left gutter needed to print `ticks` without clipping, tick mark included. */
export function axisWidth(ticks: string[]): number {
  const widest = ticks.reduce((w, t) => Math.max(w, t.length), 1);
  return Math.min(56, 10 + widest * AXIS_CHAR_W);
}

/** How many x-axis labels a plot this wide can hold before rotated dates start to collide. */
export function maxXLabels(plotWidth: number): number {
  return Math.max(2, Math.min(12, Math.floor(plotWidth / X_LABEL_SLOT)));
}

/**
 * Which of `count` points get an x-axis label, thinned to at most `maxLabels`.
 *
 * Always includes the first and the last — the range ends are the two labels a merchant looks for
 * — and drops the second-to-last pick when the final gap would be under half a step, which is
 * what stops the last two dates overprinting each other.
 */
export function thinIndices(count: number, maxLabels: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const budget = Math.max(2, Math.floor(maxLabels));
  if (count <= budget) return Array.from({ length: count }, (_, i) => i);

  const step = Math.ceil((count - 1) / (budget - 1));
  const out: number[] = [];
  for (let i = 0; i < count - 1; i += step) out.push(i);
  if (out.length > 1 && count - 1 - out[out.length - 1]! < step / 2) out.pop();
  out.push(count - 1);
  return out;
}

/**
 * `YYYY-MM-DD` → `MM-DD`.
 *
 * The year is constant across every range the page offers (7/30/90 days, this month) and printing
 * it made each rotated label half again as wide for no information. Deliberately not localised:
 * `05-08` would mean two different days to a `pt-BR` and an `en-US` merchant, while the ISO
 * month-day it already showed reads the same for everyone.
 */
export function shortDayLabel(day: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day.slice(5) : day;
}

/**
 * Radius for the per-point markers, or `0` to draw none.
 *
 * The reference design puts a dot on every point, which is right at 7 days and turns a 90-day
 * series into a solid bar of ink — so the dots fade out as the points crowd together.
 */
export function markerRadius(count: number, plotWidth: number): number {
  if (count <= 1) return 4;
  const spacing = plotWidth / (count - 1);
  if (spacing >= 14) return 3;
  // 6px apart still leaves daylight between 4px dots; a 30-day range in a narrow card sits here.
  if (spacing >= 6) return 2;
  return 0;
}

/**
 * Which point the pointer is nearest, given an x in the SVG's own pixel space.
 *
 * Nearest rather than "the segment it is inside": a merchant aiming at a dot lands on it from
 * either side, and at 90 days the dots are ~2px apart so anything stricter is unhittable. The
 * result is always a real index, so the caller never has to handle a miss — moving the pointer
 * anywhere across the plot highlights something.
 */
export function nearestIndex(px: number, left: number, plotWidth: number, count: number): number {
  if (count <= 1) return 0;
  const step = plotWidth / (count - 1);
  if (step <= 0) return 0;
  const i = Math.round((px - left) / step);
  return Math.min(count - 1, Math.max(0, i));
}

/**
 * Rough width of the hover tooltip, from its longest line.
 *
 * Estimated rather than measured on purpose: measuring means a ref, a second paint and a state
 * write on every pointer move, and the only thing the number is used for is keeping the box inside
 * the card. `axisWidth` estimates from character counts for the same reason. Capped so a long
 * money string wraps instead of growing a tooltip wider than the card.
 */
export function tooltipWidth(lines: string[]): number {
  const chars = lines.reduce((w, l) => Math.max(w, l.length), 1);
  return Math.min(180, 20 + chars * 6.2);
}

/**
 * Left edge for a tooltip that wants to be centred on `centerX`, kept inside the chart.
 *
 * Without this the tooltip for the first and last points — the two a merchant checks most —
 * hangs off the side of a 240px card and is clipped by the Nimbus card's own overflow.
 */
export function clampTooltipLeft(centerX: number, tipWidth: number, chartWidth: number): number {
  if (tipWidth >= chartWidth) return 0;
  return Math.min(Math.max(0, centerX - tipWidth / 2), chartWidth - tipWidth);
}

/**
 * A smoothed `path` through `points` (a plain polyline when `smoothing` is 0).
 *
 * Cubic control points are Catmull-Rom style but **clamped to each segment's own y range**, so the
 * curve can never bulge past its endpoints — an un-clamped spline on a spiky count series dips
 * below zero, drawing negative orders under the axis.
 */
export function linePath(points: Point[], smoothing = 0.2): string {
  if (points.length === 0) return '';
  const p = (n: Point): string => `${n.x.toFixed(2)},${n.y.toFixed(2)}`;
  if (points.length === 1) return `M${p(points[0]!)}`;

  let d = `M${p(points[0]!)}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const lo = Math.min(p1.y, p2.y);
    const hi = Math.max(p1.y, p2.y);
    const clamp = (y: number): number => Math.min(hi, Math.max(lo, y));
    const c1: Point = {
      x: p1.x + (p2.x - p0.x) * smoothing,
      y: clamp(p1.y + (p2.y - p0.y) * smoothing),
    };
    const c2: Point = {
      x: p2.x - (p3.x - p1.x) * smoothing,
      y: clamp(p2.y - (p3.y - p1.y) * smoothing),
    };
    d += ` C${p(c1)} ${p(c2)} ${p(p2)}`;
  }
  return d;
}

/** The line path closed down to `baseline`, for the area fill under it. */
export function areaPath(points: Point[], baseline: number, smoothing = 0.2): string {
  if (points.length < 2) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return `${linePath(points, smoothing)} L${last.x.toFixed(2)},${baseline.toFixed(2)} L${first.x.toFixed(2)},${baseline.toFixed(2)} Z`;
}
